import type { RelationOption } from "@bundle/core";
import { useState } from "react";
import { Icon, Pressable, ScrollView, Stack, Text, TextField } from "../../primitives/index.ts";
import { useT } from "../../i18n/I18nProvider.tsx";

export type RelationPickerProps = {
  options: RelationOption[];
  selected: string[];
  onChange: (pageIds: string[]) => void;
  onDone: () => void;
  testID: string;
};

/** Search and toggle pages of the related database. */
export function RelationPicker({ options, selected, onChange, onDone, testID }: RelationPickerProps) {
  const t = useT();
  const [query, setQuery] = useState("");
  const q = query.trim().toLowerCase();
  const visible = options.filter((o) => q === "" || o.title.toLowerCase().includes(q));
  const toggle = (id: string) => onChange(selected.includes(id) ? selected.filter((x) => x !== id) : [...selected, id]);
  return (
    <Stack gap="1" padding="2" testID={testID}>
      <TextField value={query} onChange={setQuery} placeholder={t("db.relation.search")} autoFocus onCancel={onDone} testID={`${testID}-search`} />
      <ScrollView>
        <Stack gap="0.5" paddingY="1">
          {visible.length === 0 ? (
            <Stack paddingX="2" paddingY="2">
              <Text variant="footnote" color="text.tertiary">
                {t("db.relation.empty")}
              </Text>
            </Stack>
          ) : null}
          {visible.map((o) => (
            <Pressable key={o.id} direction="horizontal" radius="sm" onPress={() => toggle(o.id)} selected={selected.includes(o.id)} testID={`${testID}-page-${o.id}`}>
              <Stack grow direction="horizontal" align="center" gap="2" paddingX="2" paddingY="1">
                {o.icon === undefined ? <Icon name="page" size="icon.sm" color="text.secondary" /> : <Text variant="footnote">{o.icon}</Text>}
                <Stack grow>
                  <Text variant="footnote" lines={1}>
                    {o.title === "" ? t("db.untitled") : o.title}
                  </Text>
                </Stack>
                {selected.includes(o.id) ? <Icon name="check" size="icon.sm" color="accent.text" /> : null}
              </Stack>
            </Pressable>
          ))}
        </Stack>
      </ScrollView>
    </Stack>
  );
}
